/**
 * TeamCrest — small team logo with letter-circle fallback.
 *
 * Renders an <img> when given a real URL; falls back to a colored
 * initial-letter tile when the URL is missing, errors out, or uses an
 * `inline:` sentinel (which mock data uses since real opponent logos aren't
 * wired in yet). Hand-tuned palette for known HLTV teams; deterministic HSL
 * for everything else so colors stay stable across renders.
 */
import { useState } from "react";
import { Box } from "rebass/styled-components";
import styled from "styled-components";

const TEAM_COLORS: Record<string, string> = {
  FaZe: "#E4002B",
  NAVI: "#FFD700",
  Vitality: "#FFE600",
  G2: "#1A1A1A",
  Spirit: "#E84D2A",
  MOUZ: "#E20303",
  Alliance: "#3CB371",
  WildLotus: "#7C5CFF",
};

const colorForName = (name: string): string => {
  if (!name) return "#444";
  if (TEAM_COLORS[name]) return TEAM_COLORS[name];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  return `hsl(${Math.abs(h) % 360}, 50%, 42%)`;
};

const CrestImg = styled.img<{ $size: number }>`
  height: ${({ $size }) => $size}px;
  object-fit: contain;
  width: ${({ $size }) => $size}px;
`;

const InitialTile = styled(Box)<{ $bg: string; $size: number }>`
  align-items: center;
  background: ${({ $bg }) => $bg};
  /* Rounded square — slight radius reads as a crest-style mark at small
     sizes, not a chiclet. */
  border-radius: ${({ $size }) => Math.max(3, Math.round($size * 0.22))}px;
  /* Subtle inner ring lifts the tile off the dark row background so
     adjacent meta-row pieces (▶ count, ROUND badge) read as discrete
     clusters instead of bleeding together. */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  /* Bumped from 0.55 → 0.62 so the letter is legible at 14-18px tile sizes
     where the previous ratio rendered the glyph too small to scan. */
  font-size: ${({ $size }) => Math.round($size * 0.62)}px;
  font-weight: 900;
  height: ${({ $size }) => $size}px;
  justify-content: center;
  letter-spacing: 0;
  line-height: 1;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.45);
  text-transform: uppercase;
  width: ${({ $size }) => $size}px;
`;

interface TeamCrestProps {
  name: string;
  logoUrl?: string;
  size?: number;
}

export const TeamCrest = ({ name, logoUrl, size = 18 }: TeamCrestProps) => {
  const [errored, setErrored] = useState(false);
  const useFallback =
    errored ||
    !logoUrl ||
    logoUrl.startsWith("inline:") ||
    logoUrl.length === 0;

  if (useFallback) {
    return (
      <InitialTile $bg={colorForName(name)} $size={size}>
        {(name || "?").charAt(0)}
      </InitialTile>
    );
  }

  return (
    <CrestImg
      src={logoUrl}
      alt={name}
      $size={size}
      onError={() => setErrored(true)}
    />
  );
};
